import React, { useEffect, useState } from 'react';

import type { StylingProps } from '@builder4/shared/types/layout';
import type { PaddingMarginValues } from '@/types/layout';
import { PaddingMarginFields } from './PaddingMarginFields';
import { MAX_PADDING } from '@/utils/constants';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useInspectorNode } from '@/hooks/useInspectorNode';

interface PaddingControlProps {
  onFocus?: () => void;
  onBlur?: () => void;
  value: PaddingMarginValues;
}

export const NodePaddingSettingsV2: React.FC<PaddingControlProps> = ({
  onFocus,
  onBlur,
  value,
}) => {
  const { id, node, actions } = useInspectorNode();
  const displayName = node?.data.displayName || '';
  const breakpoint = useAppSelector(selectBreakpoint);
  const [variant, setVariant] = useState<'locked' | 'unlocked'>('locked');
  const { setBPProp } = useBreakpointAwareProp(displayName);

  useEffect(() => {
    let hasSame = !!value.all;
    if (!hasSame) {
      // Check if all individual values are equal
      hasSame =
        value.top === value.right && value.right === value.bottom && value.bottom === value.left;
    }
    setVariant(hasSame ? 'locked' : 'unlocked');
  }, []);

  const onChangeHandler = (val: PaddingMarginValues) => {
    actions.history.ignore().setProp(id, (props: Partial<StylingProps>) => {
      setBPProp(props, 'padding', [val.top, val.right, val.bottom, val.left]);
    });
  };

  const handleChangeComplete = (
    newValues: PaddingMarginValues,
    initialValues: PaddingMarginValues,
  ) => {
    actions.history.ignore().setProp(id, (props: Partial<StylingProps>) => {
      setBPProp(props, 'padding', [
        initialValues.top,
        initialValues.right,
        initialValues.bottom,
        initialValues.left,
      ]);
    });
    actions.setProp(id, (props: Partial<StylingProps>) => {
      setBPProp(props, 'padding', [
        newValues.top,
        newValues.right,
        newValues.bottom,
        newValues.left,
      ]);
    });
  };

  return (
    <PaddingMarginFields
      label="Padding"
      className="w-full max-w-[216px]"
      value={value}
      variant={variant}
      setVariant={setVariant}
      onChange={onChangeHandler}
      onChangeComplete={handleChangeComplete}
      onFocus={onFocus}
      onBlur={onBlur}
      max={MAX_PADDING[breakpoint]}
    />
  );
};
